import { type ReactNode, useState, useEffect } from 'react';
import { Search, CheckCircle, XCircle, Info, AlertTriangle, Bell, type LucideIcon } from 'lucide-react';
import { useCountUp } from '../../hooks/useCountUp';

// ── StatsCard ───────────────────────────────────────────────────

interface StatsCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: LucideIcon;
  iconBg?: string;
  iconColor?: string;
  accent?: string;
}

export function StatsCard({ label, value, sub, icon: Icon, iconBg = 'bg-blue-50', iconColor = 'text-blue-600', accent }: StatsCardProps) {
  const isNum = typeof value === 'number';
  const animated = useCountUp(isNum ? (value as number) : 0);
  const display = isNum ? animated : value;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-fade-up">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className={`text-3xl font-extrabold text-gray-900 mt-1 ${isNum ? 'animate-count-in' : ''}`}>
            {display}
          </p>
          {sub && <p className={`text-sm font-medium mt-1 ${accent || 'text-teal-600'}`}>{sub}</p>}
        </div>
        <div className={`${iconBg} w-11 h-11 rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

// ── Skeleton ────────────────────────────────────────────────────

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-lg ${className}`} />;
}

// ── Toast ───────────────────────────────────────────────────────

interface ToastItem { id: number; message: string; type: 'success' | 'error' | 'info' }

/** Dispatch un toast depuis n'importe où dans l'app (sans contexte). */
export function toast(message: string, type: ToastItem['type'] = 'success') {
  window.dispatchEvent(new CustomEvent('acodoc:toast', { detail: { message, type } }));
}

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const { message, type } = (e as CustomEvent).detail as ToastItem;
      const id = Date.now() + Math.random();
      setItems(prev => [...prev, { id, message, type }]);
      setTimeout(() => setItems(prev => prev.filter(x => x.id !== id)), 3500);
    };
    window.addEventListener('acodoc:toast', handler);
    return () => window.removeEventListener('acodoc:toast', handler);
  }, []);

  if (!items.length) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
      {items.map(item => (
        <div
          key={item.id}
          className={`flex items-center gap-3 pl-4 pr-5 py-3 rounded-xl shadow-xl border text-sm font-semibold pointer-events-auto animate-toast-in
            ${item.type === 'success' ? 'bg-white border-emerald-200 text-emerald-800'
              : item.type === 'error'   ? 'bg-white border-red-200    text-red-800'
              :                           'bg-white border-sky-200    text-sky-800'}`}
        >
          {item.type === 'success' ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
           : item.type === 'error' ? <XCircle     className="w-4 h-4 text-red-500    shrink-0" />
           :                         <Info        className="w-4 h-4 text-sky-500    shrink-0" />}
          {item.message}
        </div>
      ))}
    </div>
  );
}

// ── ConfidenceBar ───────────────────────────────────────────────

export function ConfidenceBar({ value, size = 'md' }: { value: number; size?: 'sm' | 'md' }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 90 ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500';
  const h = size === 'sm' ? 'h-1.5' : 'h-2';
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 ${h} bg-gray-200 rounded-full overflow-hidden`}>
        <div className={`${h} ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-600 w-9 text-right">{pct}%</span>
    </div>
  );
}

// ── Initials ────────────────────────────────────────────────────

const AVATAR_COLORS = ['bg-blue-600', 'bg-emerald-600', 'bg-violet-600', 'bg-orange-600', 'bg-rose-600', 'bg-teal-600'];

export function Initials({ nom_complet, size = 'md' }: { nom_complet: string; size?: 'sm' | 'md' | 'lg' }) {
  const initial = (nom_complet?.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('') || '?').toUpperCase();
  const ci = (nom_complet?.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  const s = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm';
  return (
    <div className={`${s} ${AVATAR_COLORS[ci]} rounded-full flex items-center justify-center text-white font-bold shrink-0`}>
      {initial}
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────

export function Header({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <header className="bg-white border-b border-gray-200 flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-0 sm:h-16 shrink-0">
      <h1 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h1>
      <div className="flex items-center gap-2 flex-wrap">
        {children}
      </div>
    </header>
  );
}

// ── SearchBar ───────────────────────────────────────────────────

export function SearchBar({ placeholder = 'Rechercher...', value, onChange }: {
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm w-full sm:w-56 md:w-64 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
      />
    </div>
  );
}

// ── Status / Type styling maps ──────────────────────────────────

export const TYPE_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  passeport:             { bg: 'bg-blue-50',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-800' },
  cni:                   { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
  cv:                    { bg: 'bg-violet-50',  text: 'text-violet-700',  badge: 'bg-violet-100 text-violet-800' },
  contrat:               { bg: 'bg-red-50',     text: 'text-red-700',     badge: 'bg-red-100 text-red-800' },
  carte_vitale:          { bg: 'bg-cyan-50',    text: 'text-cyan-700',    badge: 'bg-cyan-100 text-cyan-800' },
  titre_sejour:          { bg: 'bg-orange-50',  text: 'text-orange-700',  badge: 'bg-orange-100 text-orange-800' },
  permis:                { bg: 'bg-gray-50',    text: 'text-gray-700',    badge: 'bg-gray-100 text-gray-800' },
  rib:                   { bg: 'bg-green-50',   text: 'text-green-700',   badge: 'bg-green-100 text-green-800' },
  diplome:               { bg: 'bg-yellow-50',  text: 'text-yellow-700',  badge: 'bg-yellow-100 text-yellow-800' },
  attestation_assurance: { bg: 'bg-pink-50',    text: 'text-pink-700',    badge: 'bg-pink-100 text-pink-800' },
  autre:                 { bg: 'bg-slate-50',   text: 'text-slate-600',   badge: 'bg-slate-100 text-slate-700' },
};

export const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  en_cours:   { label: 'En cours',   color: 'bg-blue-100 text-blue-700' },
  traite:     { label: 'Traité',     color: 'bg-emerald-100 text-emerald-800' },
  a_verifier: { label: 'À vérifier', color: 'bg-amber-100 text-amber-800' },
  erreur:     { label: 'Erreur',     color: 'bg-red-100 text-red-800' },
  archive:    { label: 'Archivé',    color: 'bg-slate-100 text-slate-600' },
};

export const SEVERITY_STYLES: Record<string, { bg: string; border: string; text: string; icon: string; Icon: LucideIcon }> = {
  critical: { bg: 'bg-red-50', border: 'border-l-red-600', text: 'text-red-800', icon: 'text-red-600', Icon: XCircle },
  urgent: { bg: 'bg-amber-50', border: 'border-l-amber-500', text: 'text-amber-800', icon: 'text-amber-600', Icon: AlertTriangle },
  warning: { bg: 'bg-sky-50', border: 'border-l-sky-400', text: 'text-sky-800', icon: 'text-sky-500', Icon: Bell },
  info: { bg: 'bg-slate-50', border: 'border-l-slate-400', text: 'text-slate-800', icon: 'text-slate-500', Icon: Info },
};
